import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { PROGRAMS as VOUCHER_PROGRAMS, buildSoftIntro } from "./VoucherCoalitionsTab";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  queued:   { bg: "rgba(0,255,255,0.10)",  border: "rgba(0,255,255,0.35)",  fg: "#00FFFF" },
  sent:     { bg: "rgba(0,220,120,0.10)",  border: "rgba(0,220,120,0.35)",  fg: "#00DC78" },
  failed:   { bg: "rgba(255,107,107,0.10)",border: "rgba(255,107,107,0.35)",fg: "#FF8B6B" },
  cancelled:{ bg: "rgba(255,255,255,0.04)",border: "rgba(255,255,255,0.18)",fg: "#7E92A8" },
  paused:   { bg: "rgba(255,179,71,0.10)", border: "rgba(255,179,71,0.35)", fg: "#FFB347" },
};

const PITCH_TYPES = [
  { id: "manual",          label: "Custom Pitch" },
  { id: "visualization",   label: "Visualization Preview" },
  { id: "agency_intro",    label: "Government Agency Intro" },
  { id: "reit_intro",      label: "REIT / Institutional Intro" },
];

const fmtWhen = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return iso; }
};

const relTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso).getTime();
  const now = Date.now();
  const mins = Math.round((d - now) / 60000);
  if (Math.abs(mins) < 60) return mins >= 0 ? `in ${mins}m` : `${-mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (Math.abs(hrs) < 36) return hrs >= 0 ? `in ${hrs}h` : `${-hrs}h ago`;
  const days = Math.round(hrs / 24);
  return days >= 0 ? `in ${days}d` : `${-days}d ago`;
};

const OutreachAutoPilotTab = () => {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ total: 0, queued: 0, sent: 0, failed: 0 });
  const [isPaused, setIsPaused] = useState(false);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [status, setStatus] = useState("");

  // Quick schedule form
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [pitchType, setPitchType] = useState("manual");
  const [message, setMessage] = useState("");
  const [scheduleAt, setScheduleAt] = useState("");
  const [autoFollowup, setAutoFollowup] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [prefillBanner, setPrefillBanner] = useState("");

  // Bulk Campaign state
  const [campaignOpen, setCampaignOpen] = useState(false);
  const [campaignTemplate, setCampaignTemplate] = useState(""); // "voucher" | "agencies" | "reits"
  const [campaignTargets, setCampaignTargets] = useState([]); // [{selected, prospect_name, prospect_email, ...}]
  const [campaignStartDate, setCampaignStartDate] = useState("");
  const [campaignAutoFollowup, setCampaignAutoFollowup] = useState(true);
  const [campaignSubmitting, setCampaignSubmitting] = useState(false);
  const [campaignResult, setCampaignResult] = useState(null);
  const [prospectsCache, setProspectsCache] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [exportingCal, setExportingCal] = useState(false);

  // Launch Readiness state
  const [launchOpen, setLaunchOpen] = useState(false);
  const [launchData, setLaunchData] = useState(null);
  const [launchLoading, setLaunchLoading] = useState(false);
  const [testEmailStatus, setTestEmailStatus] = useState("");

  // On mount: hydrate from sessionStorage if a prospect handed us a pre-filled outreach
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("mf_autopilot_prefill");
      if (raw) {
        const p = JSON.parse(raw);
        if (p.prospect_name) setName(p.prospect_name);
        if (p.prospect_email) setEmail(p.prospect_email);
        if (p.property_address) setAddress(p.property_address);
        if (p.pitch_type) setPitchType(p.pitch_type);
        if (p.custom_message) setMessage(p.custom_message);
        if (p.meta_company || p.meta_tier) {
          setPrefillBanner(`Pre-filled from ${p.meta_company || "Prospects"}${p.meta_tier ? ` (${p.meta_tier})` : ""} — review the message below and pick a date, or leave blank for the next optimal slot.`);
        } else {
          setPrefillBanner("Pre-filled from Prospects — review and queue.");
        }
        sessionStorage.removeItem("mf_autopilot_prefill");
        // Smooth scroll to the form
        setTimeout(() => {
          const el = document.querySelector('[data-testid="quick-schedule-card"]');
          if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
      }
    } catch {}
  }, []);

  const fetchLog = useCallback(async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/outreach/log`, { credentials: "include" });
      if (r.ok) {
        const data = await r.json();
        setItems(data.items || []);
        setCounts(data.counts || { total: 0, queued: 0, sent: 0, failed: 0 });
        setIsPaused(!!data.is_paused);
      }
    } catch {}
    try {
      const c = await fetch(`${BACKEND_URL}/api/admin/outreach/campaigns`, { credentials: "include" });
      if (c.ok) {
        const data = await c.json();
        setCampaigns(data.campaigns || []);
      }
    } catch {}
    setLoading(false);
  }, []);

  const downloadCalendar = async () => {
    setExportingCal(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/outreach/calendar.ics`, { credentials: "include" });
      if (r.ok) {
        const blob = await r.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "moovin-forward-autopilot.ics";
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch {}
    setExportingCal(false);
  };

  const downloadCSV = async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/outreach/export.csv`, { credentials: "include" });
      if (r.ok) {
        const blob = await r.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `moovin-forward-outreach-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch {}
  };

  const openLaunchModal = async () => {
    setLaunchOpen(true);
    setLaunchLoading(true);
    setTestEmailStatus("");
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/outreach/launch-readiness`, { credentials: "include" });
      if (r.ok) {
        setLaunchData(await r.json());
      }
    } catch {}
    setLaunchLoading(false);
  };

  const sendTestEmail = async () => {
    setTestEmailStatus("Sending…");
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/outreach/test-email`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({})
      });
      const data = await r.json().catch(() => ({}));
      if (r.ok && data.ok) {
        setTestEmailStatus(`✓ Sent to ${data.sent_to} — check your inbox in ~30 seconds.`);
      } else {
        setTestEmailStatus(`✗ ${data.detail || "Send failed"}`);
      }
    } catch {
      setTestEmailStatus("✗ Network error.");
    }
  };

  const togglePulse = async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/outreach/pulse-toggle`, {
        method: "POST", credentials: "include"
      });
      if (r.ok) {
        // Refetch
        const lr = await fetch(`${BACKEND_URL}/api/admin/outreach/launch-readiness`, { credentials: "include" });
        if (lr.ok) setLaunchData(await lr.json());
      }
    } catch {}
  };

  useEffect(() => {
    fetchLog();
    const t = setInterval(fetchLog, 120000);
    return () => clearInterval(t);
  }, [fetchLog]);

  const togglePause = async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/outreach/pause-toggle`, {
        method: "POST", credentials: "include"
      });
      if (r.ok) {
        const data = await r.json();
        setIsPaused(!!data.is_paused);
        setStatus(data.is_paused ? "⏸ Auto-Pilot paused. Queued items will not send until resumed." : "▶ Auto-Pilot resumed. Queued items will fire at their scheduled time.");
        setTimeout(() => setStatus(""), 4000);
      }
    } catch {}
  };

  const cancelItem = async (id) => {
    // Confirm via toast (mobile-friendly) — sonner supports action buttons.
    // No more blocking window.confirm which some mobile keyboards
    // obscured with the on-screen dismiss overlay.
    toast(
      "Cancel this scheduled outreach?",
      {
        description: "It will not be sent to the prospect.",
        duration: 8000,
        action: {
          label: "Yes, cancel it",
          onClick: async () => {
            try {
              const r = await fetch(`${BACKEND_URL}/api/admin/outreach/${id}/cancel`, {
                method: "POST",
                credentials: "include",
              });
              if (r.ok) {
                toast.success("Outreach cancelled", {
                  description: "Removed from the pipeline. It won't fire.",
                });
                fetchLog();
              } else {
                let msg = `Cancel failed (${r.status}).`;
                try { const d = await r.json(); if (d.detail) msg = d.detail; } catch {}
                toast.error("Couldn't cancel", { description: msg });
              }
            } catch (e) {
              toast.error("Network error", { description: String(e.message || e) });
            }
          },
        },
        cancel: { label: "Keep it" },
      }
    );
  };

  const scheduleNew = async () => {
    if (!name.trim() || !email.trim() || !email.includes("@")) {
      setStatus("Name and a valid email are required.");
      return;
    }
    setSubmitting(true);
    setStatus("");
    try {
      const body = {
        prospect_name: name.trim(),
        prospect_email: email.trim().toLowerCase(),
        property_address: address.trim(),
        custom_message: message.trim(),
        pitch_type: pitchType,
        auto_followup: autoFollowup,
        scheduled_send_at: scheduleAt ? new Date(scheduleAt).toISOString() : ""
      };
      const r = await fetch(`${BACKEND_URL}/api/admin/outreach/schedule`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
      });
      const data = await r.json().catch(() => ({}));
      if (r.ok && data.ok) {
        setStatus(`✓ Queued — fires ${fmtWhen(data.scheduled_send_at)} (${relTime(data.scheduled_send_at)})${autoFollowup ? " · auto-followup in 5d if no reply" : ""}`);
        setName(""); setEmail(""); setAddress(""); setMessage(""); setScheduleAt(""); setPrefillBanner("");
        fetchLog();
        setTimeout(() => setStatus(""), 6000);
      } else {
        setStatus(data.detail || "Failed to schedule. Try again.");
      }
    } catch {
      setStatus("Network error. Try again.");
    }
    setSubmitting(false);
  };

  // Next 7 days view
  const next7 = items.filter(i => {
    if (i.status !== "queued") return false;
    const t = new Date(i.scheduled_send_at).getTime();
    return t - Date.now() < 7 * 24 * 3600 * 1000;
  }).sort((a, b) => new Date(a.scheduled_send_at) - new Date(b.scheduled_send_at));

  const filtered = items.filter(i => filter === "all" ? true : i.status === filter);

  // ============= BULK CAMPAIGN =============
  const buildAgencyMessage = (p) => {
    const name = (p.decision_maker || "").split("—")[0].split("–")[0].split("|")[0].trim() || "there";
    return [
      `Hi ${name} —`,
      "",
      `I'm Shannon Ware, founder of Moovin Forward — a DFW-based interior stabilization team built specifically for ${p.tier === "housing_authority" ? "voucher-eligible and HUD-administered housing portfolios" : "high-volume institutional real estate portfolios"}.`,
      "",
      `For ${p.company} specifically: ${(p.talking_points || "").split(/\.\s/).slice(0, 2).join(". ").trim()}.`,
      "",
      p.units > 0
        ? `On a portfolio your size (${p.units.toLocaleString()} units), even a 60% turn-time compression recovers an estimated $${(Math.round(p.units * 0.1) * 1500 * 0.6 * 12).toLocaleString()}/yr in NOI.`
        : "We deliver locked per-unit pricing, NSPIRE-compliant finishes, and audit-ready Line-Itemized Packets — no requisition delays, materials on-site Day 1.",
      "",
      "Worth a 15-min call? https://calendly.com/nivoomforward",
      "",
      "— Shannon Ware",
      "Founder, Moovin Forward",
      "(817) 381-8831 · hello@moovinforward.org",
    ].join("\n");
  };

  const openCampaign = async (template) => {
    setCampaignTemplate(template);
    setCampaignResult(null);
    setCampaignStartDate("");
    setCampaignAutoFollowup(true);
    let targets = [];

    if (template === "voucher") {
      targets = VOUCHER_PROGRAMS.map(p => ({
        selected: true,
        prospect_name: p.dfw_contacts?.[0]?.name || p.full_name || p.name,
        prospect_email: "",
        property_address: "",
        custom_message: buildSoftIntro(p),
        pitch_type: "agency_intro",
        meta_label: p.full_name || p.name,
      }));
    } else if (template === "launch_sequence") {
      // Curated 4-week launch sequence: voucher coalitions first (warm), then agencies, then SFR.
      const week1 = VOUCHER_PROGRAMS.slice(0, 5).map(p => ({
        selected: true,
        prospect_name: p.dfw_contacts?.[0]?.name || p.full_name || p.name,
        prospect_email: "",
        property_address: "",
        custom_message: buildSoftIntro(p),
        pitch_type: "agency_intro",
        meta_label: `Week 1 · ${p.name}`,
      }));
      let cache = prospectsCache;
      if (!cache) {
        try {
          const r = await fetch(`${BACKEND_URL}/api/admin/prospects`, { credentials: "include" });
          if (r.ok) {
            const d = await r.json();
            cache = d.prospects || [];
            setProspectsCache(cache);
          } else { cache = []; }
        } catch { cache = []; }
      }
      const buildRow = (p, weekLabel) => {
        const dm = p.decision_maker || "";
        const emailMatch = dm.match(/[\w.-]+@[\w.-]+\.\w+/);
        const nameMatch = dm.split("—")[0].split("|")[0].split("–")[0].trim();
        return {
          selected: true,
          prospect_name: nameMatch || p.company,
          prospect_email: emailMatch ? emailMatch[0] : "",
          property_address: p.address || "",
          custom_message: buildAgencyMessage(p),
          pitch_type: p.tier === "housing_authority" ? "agency_intro" : p.tier === "institutional" ? "reit_intro" : "manual",
          meta_label: `${weekLabel} · ${p.company}`,
        };
      };
      const week2 = cache.filter(p => p.tier === "housing_authority").slice(0, 5).map(p => buildRow(p, "Week 2"));
      const week3 = cache.filter(p => p.tier === "institutional").slice(0, 5).map(p => buildRow(p, "Week 3"));
      const week4 = cache.filter(p => p.tier === "sfr" || p.tier === "local").slice(0, 5).map(p => buildRow(p, "Week 4"));
      targets = [...week1, ...week2, ...week3, ...week4];

      // Auto-fill start date with next Tuesday at 8 AM local
      const today = new Date();
      const nextTue = new Date(today);
      nextTue.setDate(today.getDate() + ((9 - today.getDay()) % 7 || 7));
      nextTue.setHours(8, 0, 0, 0);
      setCampaignStartDate(nextTue.toISOString().slice(0, 16));
    } else if (template === "agencies" || template === "reits") {
      // Lazy-load prospects once
      let cache = prospectsCache;
      if (!cache) {
        try {
          const r = await fetch(`${BACKEND_URL}/api/admin/prospects`, { credentials: "include" });
          if (r.ok) {
            const d = await r.json();
            cache = d.prospects || [];
            setProspectsCache(cache);
          } else {
            cache = [];
          }
        } catch {
          cache = [];
        }
      }
      const filterFn = template === "agencies"
        ? (p) => p.tier === "housing_authority" || p.tier === "networking" || p.tier === "grassroots"
        : (p) => p.tier === "institutional" || p.tier === "sfr" || p.tier === "distressed" || p.tier === "local";
      targets = cache.filter(filterFn).map(p => {
        const dm = p.decision_maker || "";
        const emailMatch = dm.match(/[\w.-]+@[\w.-]+\.\w+/);
        const nameMatch = dm.split("—")[0].split("|")[0].split("–")[0].trim();
        return {
          selected: true,
          prospect_name: nameMatch || p.company,
          prospect_email: emailMatch ? emailMatch[0] : "",
          property_address: p.address || "",
          custom_message: buildAgencyMessage(p),
          pitch_type: p.tier === "housing_authority" ? "agency_intro" : p.tier === "institutional" ? "reit_intro" : "manual",
          meta_label: p.company,
        };
      });
    }
    setCampaignTargets(targets);
    setCampaignOpen(true);
  };

  const updateTarget = (idx, patch) => {
    setCampaignTargets(prev => prev.map((t, i) => i === idx ? { ...t, ...patch } : t));
  };

  const submitCampaign = async () => {
    const valid = campaignTargets.filter(t => t.selected && t.prospect_email && t.prospect_email.includes("@") && t.prospect_name);
    if (valid.length === 0) {
      setCampaignResult({ error: "Add at least one valid email and select rows to queue." });
      return;
    }
    setCampaignSubmitting(true);
    setCampaignResult(null);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/outreach/campaign-bulk`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          campaign_name: campaignTemplate === "voucher" ? "Voucher Coalition Soft Intros" : campaignTemplate === "agencies" ? "Government Agencies Outreach" : campaignTemplate === "launch_sequence" ? "🚀 Launch Sequence (4-Week)" : "REIT & Institutional Outreach",
          start_date: campaignStartDate ? new Date(campaignStartDate).toISOString() : "",
          auto_followup: campaignAutoFollowup,
          targets: valid.map(t => ({
            prospect_name: t.prospect_name.trim(),
            prospect_email: t.prospect_email.trim().toLowerCase(),
            property_address: (t.property_address || "").trim(),
            custom_message: (t.custom_message || "").trim(),
            pitch_type: t.pitch_type || "manual",
          }))
        })
      });
      const data = await r.json().catch(() => ({}));
      if (r.ok && data.ok) {
        setCampaignResult({ success: true, queued: data.queued, skipped: data.skipped || [], preview: data.preview || [] });
        fetchLog();
      } else {
        setCampaignResult({ error: data.detail || "Campaign failed. Try again." });
      }
    } catch {
      setCampaignResult({ error: "Network error. Try again." });
    }
    setCampaignSubmitting(false);
  };

  const closeCampaign = () => {
    setCampaignOpen(false);
    setCampaignTemplate("");
    setCampaignTargets([]);
    setCampaignResult(null);
  };

  const validCount = campaignTargets.filter(t => t.selected && t.prospect_email && t.prospect_email.includes("@")).length;

  return (
    <div data-testid="outreach-autopilot-tab" style={{ padding: "0 0.5rem" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "1rem", marginBottom: "1.5rem" }}>
        <div>
          <h1 style={{ color: "#fff", fontSize: "1.65rem", fontWeight: 800, margin: "0 0 0.4rem", letterSpacing: "-0.01em" }}>
            Outreach Auto-Pilot
          </h1>
          <p style={{ color: "#ADBED1", fontSize: "0.92rem", margin: 0, lineHeight: 1.5, maxWidth: 680 }}>
            Queue pitches and visualization previews to fire automatically on Tue/Wed/Thu mornings (Central) — even while you're on the road. Locked per-unit pricing. NSPIRE-compliant. BCC&apos;d to <span style={{ color: "#5AC8D8" }}>hello@moovinforward.org</span> for audit trail.
          </p>
        </div>
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
          <button
            onClick={openLaunchModal}
            data-testid="btn-launch-readiness"
            style={{
              padding: "0.7rem 1.25rem",
              borderRadius: 8,
              fontWeight: 800,
              fontSize: "0.85rem",
              cursor: "pointer",
              whiteSpace: "nowrap",
              border: "1px solid #FFB347",
              background: "linear-gradient(135deg, rgba(255,179,71,0.18) 0%, rgba(0,220,120,0.12) 100%)",
              color: "#fff"
            }}
          >
            🟢 Launch Readiness
          </button>
          <button
            onClick={() => openCampaign("launch_sequence")}
            data-testid="btn-launch-campaign"
            style={{
              padding: "0.7rem 1.25rem",
              borderRadius: 8,
              fontWeight: 800,
              fontSize: "0.85rem",
              cursor: "pointer",
              whiteSpace: "nowrap",
              border: "1px solid #A78BFA",
              background: "linear-gradient(135deg, rgba(167,139,250,0.18) 0%, rgba(0,255,255,0.10) 100%)",
              color: "#fff"
            }}
          >
            🚀 Bulk Campaign
          </button>
          <button
            onClick={downloadCalendar}
            disabled={exportingCal}
            data-testid="btn-export-calendar"
            title="Download all queued + sent outreach as a .ics calendar file. Open it on your phone to add to Apple Calendar / Google Calendar."
            style={{
              padding: "0.7rem 1.25rem",
              borderRadius: 8,
              fontWeight: 800,
              fontSize: "0.85rem",
              cursor: exportingCal ? "wait" : "pointer",
              whiteSpace: "nowrap",
              border: "1px solid rgba(90,200,216,0.50)",
              background: "rgba(90,200,216,0.10)",
              color: "#5AC8D8",
              opacity: exportingCal ? 0.6 : 1
            }}
          >
            {exportingCal ? "Exporting…" : "📅 Export to Calendar"}
          </button>
          <button
            onClick={togglePause}
            data-testid="btn-toggle-pause"
            style={{
              padding: "0.7rem 1.25rem",
              borderRadius: 8,
              fontWeight: 800,
              fontSize: "0.85rem",
              cursor: "pointer",
              whiteSpace: "nowrap",
              border: `1px solid ${isPaused ? "#FFB347" : "#00DC78"}`,
              background: isPaused ? "rgba(255,179,71,0.12)" : "rgba(0,220,120,0.10)",
              color: isPaused ? "#FFB347" : "#00DC78"
            }}
          >
            {isPaused ? "⏸ Pipeline PAUSED — Resume" : "▶ Pipeline LIVE — Pause"}
          </button>
        </div>
      </div>

      {/* KPI strip */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: "0.75rem", marginBottom: "1.5rem" }}>
        {[
          { k: "queued", l: "Queued",  fg: "#00FFFF" },
          { k: "sent",   l: "Sent",    fg: "#00DC78" },
          { k: "failed", l: "Failed",  fg: "#FF8B6B" },
          { k: "total",  l: "All-Time",fg: "#ADBED1" },
        ].map(s => (
          <div key={s.k} data-testid={`kpi-${s.k}`} style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, padding: "1rem 1.1rem" }}>
            <div style={{ color: "#7E92A8", fontSize: "0.7rem", letterSpacing: "0.12em", fontWeight: 700, textTransform: "uppercase" }}>{s.l}</div>
            <div style={{ color: s.fg, fontSize: "1.7rem", fontWeight: 800, marginTop: "0.25rem" }}>{counts[s.k] ?? 0}</div>
          </div>
        ))}
      </div>

      {/* Quick Schedule */}
      <div data-testid="quick-schedule-card" style={{ background: "linear-gradient(135deg, rgba(0,255,255,0.04) 0%, rgba(0,220,120,0.04) 100%)", border: "1px solid rgba(0,255,255,0.18)", borderRadius: 12, padding: "1.5rem 1.75rem", marginBottom: "1.75rem" }}>
        {prefillBanner && (
          <div data-testid="prefill-banner" style={{ marginBottom: "0.95rem", padding: "0.7rem 0.95rem", background: "rgba(0,220,120,0.08)", border: "1px solid rgba(0,220,120,0.35)", borderRadius: 6, color: "#00DC78", fontSize: "0.82rem", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.85rem", flexWrap: "wrap" }}>
            <span>✓ {prefillBanner}</span>
            <button onClick={() => setPrefillBanner("")} data-testid="dismiss-prefill" style={{ background: "transparent", border: "1px solid rgba(0,220,120,0.4)", color: "#00DC78", padding: "0.25rem 0.6rem", borderRadius: 4, fontSize: "0.7rem", fontWeight: 700, cursor: "pointer" }}>Dismiss</button>
          </div>
        )}
        <div style={{ marginBottom: "1rem" }}>
          <h3 style={{ color: "#00FFFF", fontSize: "1rem", fontWeight: 800, margin: "0 0 0.35rem" }}>+ Schedule a New Outreach</h3>
          <p style={{ color: "#ADBED1", fontSize: "0.8rem", margin: 0, lineHeight: 1.5 }}>
            Leave the date blank and we'll pick the next optimal slot (Tue/Wed/Thu @ 8 / 9:30 / 11 AM Central, staggered).
            For visualization-rich emails with before/after images, use the <strong style={{ color: "#fff" }}>Design Studio</strong> instead and toggle &ldquo;Schedule for later.&rdquo;
          </p>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.7rem", marginBottom: "0.7rem" }}>
          <div className="ds-input-group">
            <label style={lbl}>Prospect Name *</label>
            <input data-testid="ap-name" style={inp} type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Kenneth Duke" />
          </div>
          <div className="ds-input-group">
            <label style={lbl}>Prospect Email *</label>
            <input data-testid="ap-email" style={inp} type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="kenneth.duke@dhantx.com" />
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: "0.7rem", marginBottom: "0.7rem" }}>
          <div className="ds-input-group">
            <label style={lbl}>Property / Portfolio (optional)</label>
            <input data-testid="ap-address" style={inp} type="text" value={address} onChange={e => setAddress(e.target.value)} placeholder="Roseland RAD Conversion — Dallas" />
          </div>
          <div className="ds-input-group">
            <label style={lbl}>Pitch Type</label>
            <select data-testid="ap-pitch-type" style={inp} value={pitchType} onChange={e => setPitchType(e.target.value)}>
              {PITCH_TYPES.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
          </div>
          <div className="ds-input-group">
            <label style={lbl}>Send At (blank = auto)</label>
            <input data-testid="ap-schedule-at" style={inp} type="datetime-local" value={scheduleAt} onChange={e => setScheduleAt(e.target.value)} />
          </div>
        </div>

        <div className="ds-input-group" style={{ marginBottom: "0.7rem" }}>
          <label style={lbl}>Personal Pitch Message</label>
          <textarea
            data-testid="ap-message"
            rows="4"
            value={message}
            onChange={e => setMessage(e.target.value)}
            placeholder={"Kenneth — following up on our DHA Roseland conversation. Phased Year 1 pilot of 50 units, audit-ready Line-Itemized Packets included. Open Tue/Wed next week..."}
            style={{ ...inp, resize: "vertical", minHeight: 90, fontFamily: "inherit" }}
          />
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.65rem 0.85rem", background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 6, marginBottom: "0.7rem" }}>
          <input
            type="checkbox"
            id="ap-auto-followup"
            checked={autoFollowup}
            onChange={e => setAutoFollowup(e.target.checked)}
            data-testid="ap-auto-followup"
            style={{ accentColor: "#00DC78", width: 16, height: 16, cursor: "pointer" }}
          />
          <label htmlFor="ap-auto-followup" style={{ color: "#fff", fontSize: "0.82rem", fontWeight: 600, cursor: "pointer", margin: 0, display: "flex", flexDirection: "column", gap: "0.15rem" }}>
            <span>Auto-queue follow-up bump in 5 days if no reply</span>
            <span style={{ color: "#7E92A8", fontSize: "0.72rem", fontWeight: 500, fontStyle: "italic" }}>
              Recommended. The system queues a templated nudge on the next Tue/Wed/Thu @ 9:30 AM CT after this fires. You can cancel it from the log if they reply.
            </span>
          </label>
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "0.75rem" }}>
          {status ? (
            <p data-testid="ap-status" style={{ margin: 0, fontSize: "0.82rem", fontWeight: 600, color: status.startsWith("✓") || status.startsWith("▶") ? "#00DC78" : status.startsWith("⏸") ? "#FFB347" : "#FF8B6B" }}>{status}</p>
          ) : <span />}
          <button
            data-testid="btn-schedule-outreach"
            onClick={scheduleNew}
            disabled={submitting}
            style={{ background: "linear-gradient(135deg, #00DC78 0%, #00FFFF 100%)", color: "#030B1A", border: "none", padding: "0.85rem 1.6rem", borderRadius: 8, fontWeight: 800, fontSize: "0.88rem", cursor: submitting ? "not-allowed" : "pointer", opacity: submitting ? 0.6 : 1, whiteSpace: "nowrap" }}
          >
            {submitting ? "Queueing…" : "📅 Queue Outreach"}
          </button>
        </div>
      </div>

      {/* Next 7 Days */}
      {next7.length > 0 && (
        <div data-testid="next-7-days" style={{ marginBottom: "1.75rem" }}>
          <h3 style={{ color: "#fff", fontSize: "1.05rem", fontWeight: 800, margin: "0 0 0.85rem" }}>
            🗓 Next 7 Days <span style={{ color: "#7E92A8", fontWeight: 600, fontSize: "0.8rem", marginLeft: "0.4rem" }}>{next7.length} firing soon</span>
          </h3>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))", gap: "0.75rem" }}>
            {next7.slice(0, 8).map(it => (
              <div key={it.outreach_id} data-testid={`pipeline-${it.outreach_id}`} style={{ background: "rgba(0,255,255,0.04)", border: "1px solid rgba(0,255,255,0.20)", borderRadius: 10, padding: "0.95rem 1.1rem" }}>
                <div style={{ color: "#00FFFF", fontSize: "0.7rem", letterSpacing: "0.12em", fontWeight: 800, textTransform: "uppercase", marginBottom: "0.4rem" }}>
                  {fmtWhen(it.scheduled_send_at)} <span style={{ color: "#7E92A8", marginLeft: 4 }}>({relTime(it.scheduled_send_at)})</span>
                </div>
                <div style={{ color: "#fff", fontSize: "0.95rem", fontWeight: 700, marginBottom: "0.2rem" }}>{it.prospect_name}</div>
                <div style={{ color: "#ADBED1", fontSize: "0.8rem", marginBottom: "0.55rem" }}>{it.prospect_email}</div>
                {it.property_address && (
                  <div style={{ color: "#7E92A8", fontSize: "0.74rem", marginBottom: "0.55rem", fontStyle: "italic" }}>{it.property_address}</div>
                )}
                <button
                  onClick={() => cancelItem(it.outreach_id)}
                  data-testid={`btn-cancel-${it.outreach_id}`}
                  style={{ background: "transparent", border: "1px solid rgba(255,107,107,0.4)", color: "#FF8B6B", padding: "0.35rem 0.7rem", borderRadius: 6, fontSize: "0.72rem", fontWeight: 700, cursor: "pointer" }}
                >
                  Cancel
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Campaign Analytics */}
      {campaigns.length > 0 && (
        <div data-testid="campaigns-analytics" style={{ marginBottom: "1.75rem" }}>
          <h3 style={{ color: "#fff", fontSize: "1.05rem", fontWeight: 800, margin: "0 0 0.85rem" }}>
            📊 Campaign Analytics <span style={{ color: "#7E92A8", fontWeight: 600, fontSize: "0.8rem", marginLeft: "0.4rem" }}>{campaigns.length} campaign{campaigns.length === 1 ? "" : "s"}</span>
          </h3>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: "0.75rem" }}>
            {campaigns.map(c => {
              const sent = c.sent || 0;
              const total = c.total || 1;
              const pctSent = Math.round((sent / total) * 100);
              const pctFail = Math.round(((c.failed || 0) / total) * 100);
              return (
                <div key={c.campaign_id} data-testid={`campaign-${c.campaign_id}`} style={{ background: "rgba(167,139,250,0.04)", border: "1px solid rgba(167,139,250,0.20)", borderRadius: 10, padding: "1rem 1.15rem" }}>
                  <div style={{ color: "#A78BFA", fontSize: "0.7rem", letterSpacing: "0.10em", fontWeight: 800, textTransform: "uppercase", marginBottom: "0.3rem" }}>Campaign</div>
                  <h4 style={{ color: "#fff", fontSize: "1rem", fontWeight: 800, margin: "0 0 0.6rem", lineHeight: 1.3 }}>{c.campaign_name}</h4>

                  {/* Progress bar */}
                  <div style={{ height: 8, background: "rgba(255,255,255,0.05)", borderRadius: 4, overflow: "hidden", display: "flex", marginBottom: "0.6rem" }}>
                    <div title={`${sent} sent`} style={{ width: `${pctSent}%`, background: "#00DC78", transition: "width 0.4s ease" }} />
                    <div title={`${c.failed || 0} failed`} style={{ width: `${pctFail}%`, background: "#FF8B6B" }} />
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "0.4rem", fontSize: "0.74rem" }}>
                    <Stat label="Total"     val={c.total}     color="#ADBED1" />
                    <Stat label="Sent"      val={c.sent}      color="#00DC78" />
                    <Stat label="Queued"    val={c.queued}    color="#00FFFF" />
                    <Stat label="Failed"    val={c.failed}    color="#FF8B6B" />
                  </div>

                  <div style={{ marginTop: "0.65rem", color: "#7E92A8", fontSize: "0.72rem" }}>
                    {c.first_scheduled && <>First send: {fmtWhen(c.first_scheduled)}</>}
                    {c.last_scheduled && c.last_scheduled !== c.first_scheduled && <> · Last: {fmtWhen(c.last_scheduled)}</>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Activity Log */}
      <div data-testid="activity-log">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "0.75rem", marginBottom: "0.85rem" }}>
          <h3 style={{ color: "#fff", fontSize: "1.05rem", fontWeight: 800, margin: 0 }}>Activity Log</h3>
          <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap", alignItems: "center" }}>
            {["all", "queued", "sent", "failed", "cancelled"].map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                data-testid={`filter-${f}`}
                style={{
                  background: filter === f ? "rgba(0,255,255,0.12)" : "transparent",
                  border: `1px solid ${filter === f ? "#00FFFF" : "rgba(255,255,255,0.15)"}`,
                  color: filter === f ? "#00FFFF" : "#ADBED1",
                  padding: "0.4rem 0.85rem", borderRadius: 6, fontSize: "0.74rem", fontWeight: 700, cursor: "pointer", textTransform: "capitalize"
                }}
              >
                {f}
              </button>
            ))}
            <button
              onClick={downloadCSV}
              data-testid="btn-export-csv"
              title="Download all outreach as CSV — perfect for your CPA or quarterly reports."
              style={{
                background: "transparent",
                border: "1px solid rgba(255,255,255,0.15)",
                color: "#7E92A8",
                padding: "0.4rem 0.85rem", borderRadius: 6, fontSize: "0.74rem", fontWeight: 700, cursor: "pointer"
              }}
            >
              ⬇ CSV
            </button>
          </div>
        </div>

        {loading ? (
          <p style={{ color: "#7E92A8", fontSize: "0.85rem" }}>Loading…</p>
        ) : filtered.length === 0 ? (
          <div style={{ background: "rgba(255,255,255,0.02)", border: "1px dashed rgba(255,255,255,0.15)", borderRadius: 10, padding: "2rem", textAlign: "center" }}>
            <p style={{ color: "#7E92A8", fontSize: "0.9rem", margin: 0 }}>
              No outreach {filter === "all" ? "yet" : filter}. Queue your first pitch above — or jump to <strong style={{ color: "#fff" }}>Design Studio</strong> to schedule a visualization preview.
            </p>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            {filtered.map(it => {
              const c = STATUS_COLORS[it.status] || STATUS_COLORS.queued;
              return (
                <div key={it.outreach_id} data-testid={`row-${it.outreach_id}`} style={{ background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 8, padding: "0.85rem 1.1rem", display: "grid", gridTemplateColumns: "auto 1fr auto auto", gap: "0.85rem", alignItems: "center" }}>
                  <span style={{ background: c.bg, border: `1px solid ${c.border}`, color: c.fg, padding: "0.2rem 0.6rem", borderRadius: 4, fontSize: "0.66rem", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.08em" }}>{it.status}</span>
                  <div>
                    <div style={{ color: "#fff", fontSize: "0.88rem", fontWeight: 700 }}>
                      {it.is_followup && <span title="Auto-queued follow-up bump" style={{ color: "#A78BFA", fontWeight: 800, marginRight: 6 }}>↳</span>}
                      {it.prospect_name} <span style={{ color: "#7E92A8", fontWeight: 500 }}>·</span> <span style={{ color: "#5AC8D8", fontWeight: 500 }}>{it.prospect_email}</span>
                    </div>
                    <div style={{ color: "#7E92A8", fontSize: "0.74rem", marginTop: "0.2rem" }}>
                      {it.is_followup ? "Follow-up Bump" : it.pitch_type === "visualization" ? "Visualization" : it.pitch_type === "agency_intro" ? "Agency Intro" : it.pitch_type === "reit_intro" ? "REIT Intro" : "Custom Pitch"}
                      {it.property_address && <> · {it.property_address}</>}
                      {it.palette_name && <> · {it.palette_name}</>}
                      {it.status === "sent" && it.followup_queued_id && <span style={{ color: "#A78BFA" }}> · ↳ follow-up auto-queued</span>}
                      {it.error && <span style={{ color: "#FF8B6B" }}> · {it.error}</span>}
                    </div>
                  </div>
                  <div style={{ textAlign: "right", color: "#ADBED1", fontSize: "0.78rem" }}>
                    <div>{fmtWhen(it.status === "sent" ? it.sent_at : it.scheduled_send_at)}</div>
                    <div style={{ color: "#7E92A8", fontSize: "0.7rem", marginTop: "0.15rem" }}>
                      {it.status === "sent" ? `sent ${relTime(it.sent_at)}` : it.status === "queued" ? relTime(it.scheduled_send_at) : ""}
                    </div>
                  </div>
                  {it.status === "queued" ? (
                    <button
                      onClick={() => cancelItem(it.outreach_id)}
                      data-testid={`btn-row-cancel-${it.outreach_id}`}
                      style={{ background: "transparent", border: "1px solid rgba(255,107,107,0.4)", color: "#FF8B6B", padding: "0.35rem 0.75rem", borderRadius: 6, fontSize: "0.72rem", fontWeight: 700, cursor: "pointer" }}
                    >
                      Cancel
                    </button>
                  ) : <span />}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ============= LAUNCH READINESS MODAL ============= */}
      {launchOpen && (
        <div
          data-testid="launch-modal-overlay"
          onClick={() => setLaunchOpen(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(3,11,26,0.85)", backdropFilter: "blur(6px)", zIndex: 1000, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "3rem 1.5rem", overflowY: "auto" }}
        >
          <div
            onClick={e => e.stopPropagation()}
            data-testid="launch-modal"
            style={{ background: "#0a1929", border: `1px solid ${launchData?.overall === "green" ? "rgba(0,220,120,0.40)" : launchData?.overall === "yellow" ? "rgba(255,179,71,0.40)" : "rgba(255,107,107,0.40)"}`, borderRadius: 14, maxWidth: 720, width: "100%", padding: "1.75rem 2rem", boxShadow: "0 20px 60px rgba(0,0,0,0.6)" }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "1.25rem" }}>
              <div>
                <div style={{ display: "inline-block", padding: "0.25rem 0.7rem", borderRadius: 4, fontSize: "0.68rem", fontWeight: 800, letterSpacing: "0.10em", textTransform: "uppercase", marginBottom: "0.5rem", background: launchData?.overall === "green" ? "rgba(0,220,120,0.15)" : launchData?.overall === "yellow" ? "rgba(255,179,71,0.15)" : "rgba(255,107,107,0.15)", color: launchData?.overall === "green" ? "#00DC78" : launchData?.overall === "yellow" ? "#FFB347" : "#FF8B6B", border: `1px solid ${launchData?.overall === "green" ? "rgba(0,220,120,0.35)" : launchData?.overall === "yellow" ? "rgba(255,179,71,0.35)" : "rgba(255,107,107,0.35)"}` }}>
                  {launchData?.overall === "green" ? "🟢 ALL SYSTEMS GO" : launchData?.overall === "yellow" ? "🟡 ATTENTION NEEDED" : launchData?.overall === "red" ? "🔴 BLOCKER FOUND" : "Loading…"}
                </div>
                <h2 style={{ color: "#fff", fontSize: "1.4rem", fontWeight: 800, margin: "0 0 0.3rem" }}>Launch Readiness Checklist</h2>
                <p style={{ color: "#ADBED1", fontSize: "0.86rem", margin: 0, lineHeight: 1.5 }}>
                  Every system that needs to be green before you publicly launch. Run a real test email and confirm everything fires.
                </p>
              </div>
              <button onClick={() => setLaunchOpen(false)} data-testid="btn-close-launch" style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.20)", color: "#ADBED1", padding: "0.4rem 0.85rem", borderRadius: 6, fontSize: "0.78rem", fontWeight: 700, cursor: "pointer" }}>✕ Close</button>
            </div>

            {launchLoading ? (
              <p style={{ color: "#7E92A8", fontSize: "0.85rem" }}>Running checks…</p>
            ) : launchData ? (
              <>
                <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", marginBottom: "1.25rem" }}>
                  {launchData.checks.map(c => {
                    const colors = { green: "#00DC78", yellow: "#FFB347", red: "#FF8B6B" };
                    const icons = { green: "✓", yellow: "!", red: "✗" };
                    return (
                      <div key={c.id} data-testid={`check-${c.id}`} style={{ display: "flex", alignItems: "flex-start", gap: "0.75rem", padding: "0.75rem 0.95rem", background: "rgba(255,255,255,0.025)", border: `1px solid ${colors[c.status]}33`, borderRadius: 8 }}>
                        <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 24, height: 24, borderRadius: 12, background: `${colors[c.status]}22`, color: colors[c.status], fontWeight: 800, fontSize: "0.85rem", flexShrink: 0, marginTop: 1 }}>{icons[c.status]}</span>
                        <div style={{ flex: 1 }}>
                          <div style={{ color: "#fff", fontSize: "0.88rem", fontWeight: 700 }}>{c.label}</div>
                          <div style={{ color: "#ADBED1", fontSize: "0.78rem", marginTop: "0.15rem", lineHeight: 1.5 }}>{c.detail}</div>
                          {c.action && <div style={{ color: colors[c.status], fontSize: "0.74rem", marginTop: "0.3rem", fontStyle: "italic" }}>→ {c.action}</div>}
                        </div>
                        {c.id === "pulse" && (
                          <button onClick={togglePulse} data-testid="btn-toggle-pulse" style={{ background: "transparent", border: `1px solid ${launchData.summary.pulse_enabled ? "#FFB347" : "#00DC78"}`, color: launchData.summary.pulse_enabled ? "#FFB347" : "#00DC78", padding: "0.3rem 0.65rem", borderRadius: 4, fontSize: "0.7rem", fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>
                            {launchData.summary.pulse_enabled ? "Turn OFF" : "Turn ON"}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>

                <div style={{ background: "rgba(0,255,255,0.04)", border: "1px solid rgba(0,255,255,0.20)", borderRadius: 8, padding: "1rem 1.15rem", marginBottom: "0.75rem" }}>
                  <h4 style={{ color: "#00FFFF", fontSize: "0.88rem", fontWeight: 800, margin: "0 0 0.5rem" }}>📨 Send a real test email</h4>
                  <p style={{ color: "#ADBED1", fontSize: "0.78rem", margin: "0 0 0.75rem", lineHeight: 1.5 }}>
                    Fires a real Resend email to <strong style={{ color: "#fff" }}>your account email</strong>. Confirms the sending pipeline before you queue real outreach.
                  </p>
                  <div style={{ display: "flex", gap: "0.6rem", alignItems: "center", flexWrap: "wrap" }}>
                    <button onClick={sendTestEmail} data-testid="btn-send-test-email" style={{ background: "linear-gradient(135deg, #00DC78 0%, #00FFFF 100%)", color: "#030B1A", border: "none", padding: "0.6rem 1.15rem", borderRadius: 6, fontSize: "0.8rem", fontWeight: 800, cursor: "pointer" }}>
                      📨 Send Test Email
                    </button>
                    {testEmailStatus && (
                      <span data-testid="test-email-status" style={{ color: testEmailStatus.startsWith("✓") ? "#00DC78" : testEmailStatus.startsWith("✗") ? "#FF8B6B" : "#ADBED1", fontSize: "0.78rem", fontWeight: 600 }}>
                        {testEmailStatus}
                      </span>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <p style={{ color: "#FF8B6B", fontSize: "0.85rem" }}>Could not load readiness data.</p>
            )}
          </div>
        </div>
      )}

      {/* ============= BULK CAMPAIGN MODAL ============= */}
      {campaignOpen && (
        <div
          data-testid="campaign-modal-overlay"
          onClick={closeCampaign}
          style={{ position: "fixed", inset: 0, background: "rgba(3,11,26,0.85)", backdropFilter: "blur(6px)", zIndex: 1000, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "3rem 1.5rem", overflowY: "auto" }}
        >
          <div
            onClick={e => e.stopPropagation()}
            data-testid="campaign-modal"
            style={{ background: "#0a1929", border: "1px solid rgba(167,139,250,0.30)", borderRadius: 14, maxWidth: 920, width: "100%", padding: "1.75rem 2rem", boxShadow: "0 20px 60px rgba(0,0,0,0.6)" }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "1.25rem" }}>
              <div>
                <h2 style={{ color: "#fff", fontSize: "1.4rem", fontWeight: 800, margin: "0 0 0.3rem" }}>🚀 Bulk Campaign</h2>
                <p style={{ color: "#ADBED1", fontSize: "0.86rem", margin: 0, lineHeight: 1.5 }}>
                  Pick a campaign template, add emails, set a start date — we&apos;ll stagger every send across optimal Tue/Wed/Thu × 8 / 9:30 / 11 AM CT slots. Auto-followup queues a 5-day bump for each one.
                </p>
              </div>
              <button onClick={closeCampaign} data-testid="btn-close-campaign" style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.20)", color: "#ADBED1", padding: "0.4rem 0.85rem", borderRadius: 6, fontSize: "0.78rem", fontWeight: 700, cursor: "pointer" }}>✕ Close</button>
            </div>

            {/* Template picker */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "0.6rem", marginBottom: "1.25rem" }}>
              {[
                { id: "launch_sequence", label: "🚀 Launch Sequence", subtitle: "4-week curated mix · auto-paced for launch", color: "#FFB347" },
                { id: "voucher",  label: "Voucher Coalitions", subtitle: `${VOUCHER_PROGRAMS.length} programs · soft intros`, color: "#A78BFA" },
                { id: "agencies", label: "Government Agencies", subtitle: "Housing Authorities + DFW networks", color: "#5AC8D8" },
                { id: "reits",    label: "REITs & Institutional", subtitle: "Top 50 NMHC owners + SFR portfolios", color: "#00DC78" },
              ].map(t => {
                const active = campaignTemplate === t.id;
                return (
                  <button
                    key={t.id}
                    onClick={() => openCampaign(t.id)}
                    data-testid={`campaign-tpl-${t.id}`}
                    style={{
                      textAlign: "left",
                      padding: "0.85rem 1rem",
                      background: active ? `${t.color}1F` : "rgba(255,255,255,0.025)",
                      border: `1px solid ${active ? t.color : "rgba(255,255,255,0.10)"}`,
                      borderRadius: 8,
                      cursor: "pointer",
                      color: "#fff"
                    }}
                  >
                    <div style={{ fontWeight: 800, fontSize: "0.88rem", color: active ? t.color : "#fff" }}>{t.label}</div>
                    <div style={{ color: "#7E92A8", fontSize: "0.72rem", marginTop: "0.2rem" }}>{t.subtitle}</div>
                  </button>
                );
              })}
            </div>

            {campaignTemplate && (
              <>
                {/* Settings row */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "0.85rem", marginBottom: "1rem", alignItems: "center" }}>
                  <div>
                    <label style={{ ...lbl, marginBottom: "0.25rem" }}>Start Date (blank = next available slot)</label>
                    <input
                      type="datetime-local"
                      value={campaignStartDate}
                      onChange={e => setCampaignStartDate(e.target.value)}
                      data-testid="campaign-start-date"
                      style={{ ...inp, maxWidth: 280 }}
                    />
                  </div>
                  <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", color: "#fff", fontSize: "0.82rem", fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}>
                    <input
                      type="checkbox"
                      checked={campaignAutoFollowup}
                      onChange={e => setCampaignAutoFollowup(e.target.checked)}
                      data-testid="campaign-auto-followup"
                      style={{ accentColor: "#00DC78", width: 16, height: 16, cursor: "pointer" }}
                    />
                    Auto-followup 5-day bump
                  </label>
                </div>

                {/* Targets table */}
                <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8, padding: "0.5rem", maxHeight: 360, overflowY: "auto", marginBottom: "1rem" }}>
                  <div style={{ display: "grid", gridTemplateColumns: "auto 1.4fr 1.4fr auto", gap: "0.5rem", padding: "0.5rem 0.75rem", borderBottom: "1px solid rgba(255,255,255,0.08)", color: "#7E92A8", fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", position: "sticky", top: 0, background: "#0a1929", zIndex: 1 }}>
                    <span>Use</span>
                    <span>Name</span>
                    <span>Email *</span>
                    <span>Type</span>
                  </div>
                  {campaignTargets.length === 0 ? (
                    <p style={{ color: "#7E92A8", fontSize: "0.8rem", padding: "1rem", textAlign: "center" }}>No targets loaded.</p>
                  ) : campaignTargets.map((t, idx) => (
                    <div key={idx} data-testid={`campaign-row-${idx}`} style={{ display: "grid", gridTemplateColumns: "auto 1.4fr 1.4fr auto", gap: "0.5rem", padding: "0.5rem 0.75rem", borderBottom: "1px solid rgba(255,255,255,0.04)", alignItems: "center" }}>
                      <input
                        type="checkbox"
                        checked={t.selected}
                        onChange={e => updateTarget(idx, { selected: e.target.checked })}
                        data-testid={`campaign-row-select-${idx}`}
                        style={{ accentColor: "#00FFFF", width: 16, height: 16, cursor: "pointer" }}
                      />
                      <input
                        type="text"
                        value={t.prospect_name}
                        onChange={e => updateTarget(idx, { prospect_name: e.target.value })}
                        data-testid={`campaign-row-name-${idx}`}
                        style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.08)", color: "#fff", padding: "0.4rem 0.55rem", borderRadius: 4, fontSize: "0.78rem", fontFamily: "inherit", boxSizing: "border-box" }}
                      />
                      <input
                        type="email"
                        placeholder="add email"
                        value={t.prospect_email}
                        onChange={e => updateTarget(idx, { prospect_email: e.target.value })}
                        data-testid={`campaign-row-email-${idx}`}
                        style={{ background: t.prospect_email && t.prospect_email.includes("@") ? "rgba(0,220,120,0.06)" : "rgba(255,179,71,0.06)", border: `1px solid ${t.prospect_email && t.prospect_email.includes("@") ? "rgba(0,220,120,0.30)" : "rgba(255,179,71,0.30)"}`, color: "#fff", padding: "0.4rem 0.55rem", borderRadius: 4, fontSize: "0.78rem", fontFamily: "inherit", boxSizing: "border-box" }}
                      />
                      <span style={{ color: "#7E92A8", fontSize: "0.7rem", fontWeight: 700, padding: "0 0.5rem", whiteSpace: "nowrap" }}>{t.pitch_type === "agency_intro" ? "Agency" : t.pitch_type === "reit_intro" ? "REIT" : "Custom"}</span>
                    </div>
                  ))}
                </div>

                {/* Result / submit row */}
                {campaignResult && campaignResult.success && (
                  <div data-testid="campaign-success" style={{ marginBottom: "0.85rem", padding: "0.85rem 1rem", background: "rgba(0,220,120,0.10)", border: "1px solid rgba(0,220,120,0.35)", borderRadius: 6, color: "#00DC78", fontSize: "0.85rem" }}>
                    ✓ Queued <strong>{campaignResult.queued}</strong> outreach{campaignResult.queued === 1 ? "" : "es"} across optimal slots.
                    {campaignResult.skipped?.length > 0 && (
                      <div style={{ marginTop: "0.4rem", color: "#FFB347", fontSize: "0.78rem" }}>
                        ⚠ {campaignResult.skipped.length} skipped (missing email): {campaignResult.skipped.slice(0, 3).map(s => s.name).join(", ")}{campaignResult.skipped.length > 3 ? "…" : ""}
                      </div>
                    )}
                    {campaignResult.preview?.length > 0 && (
                      <div style={{ marginTop: "0.5rem", color: "#9FE4BC", fontSize: "0.76rem" }}>
                        First sends: {campaignResult.preview.map(p => `${p.prospect_name.split(" ")[0]} ${fmtWhen(p.scheduled_send_at)}`).join(" · ")}
                      </div>
                    )}
                  </div>
                )}
                {campaignResult && campaignResult.error && (
                  <div data-testid="campaign-error" style={{ marginBottom: "0.85rem", padding: "0.85rem 1rem", background: "rgba(255,107,107,0.10)", border: "1px solid rgba(255,107,107,0.35)", borderRadius: 6, color: "#FF8B6B", fontSize: "0.85rem" }}>
                    ✗ {campaignResult.error}
                  </div>
                )}

                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "0.75rem" }}>
                  <p style={{ margin: 0, color: "#ADBED1", fontSize: "0.8rem" }}>
                    Ready to queue: <strong style={{ color: "#00DC78" }}>{validCount}</strong> of {campaignTargets.length}
                    {campaignAutoFollowup && validCount > 0 && (
                      <span style={{ color: "#7E92A8" }}> · plus {validCount} auto-followups in 5 days = <strong style={{ color: "#A78BFA" }}>{validCount * 2}</strong> total emails</span>
                    )}
                  </p>
                  <button
                    onClick={submitCampaign}
                    disabled={campaignSubmitting || validCount === 0}
                    data-testid="btn-submit-campaign"
                    style={{ background: validCount > 0 && !campaignSubmitting ? "linear-gradient(135deg, #A78BFA 0%, #00FFFF 100%)" : "rgba(255,255,255,0.05)", color: validCount > 0 && !campaignSubmitting ? "#030B1A" : "#7E92A8", border: "none", padding: "0.85rem 1.6rem", borderRadius: 8, fontWeight: 800, fontSize: "0.88rem", cursor: campaignSubmitting || validCount === 0 ? "not-allowed" : "pointer", whiteSpace: "nowrap" }}
                  >
                    {campaignSubmitting ? "Queueing…" : `🚀 Queue Campaign (${validCount})`}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const lbl = { color: "#7E92A8", fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: "0.3rem", display: "block" };
const inp = { width: "100%", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.12)", color: "#fff", padding: "0.6rem 0.75rem", borderRadius: 6, fontSize: "0.85rem", fontFamily: "inherit", boxSizing: "border-box" };

const Stat = ({ label, val, color }) => (
  <div style={{ background: "rgba(255,255,255,0.025)", borderRadius: 6, padding: "0.4rem 0.55rem", textAlign: "center" }}>
    <div style={{ color: "#7E92A8", fontSize: "0.62rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>{label}</div>
    <div style={{ color, fontSize: "1.05rem", fontWeight: 800, marginTop: "0.15rem" }}>{val ?? 0}</div>
  </div>
);

export default OutreachAutoPilotTab;
